
      import Alpine from 'alpinejs';
      import { auth, search, settings } from '/src/services/api.js';
      import { session } from '/src/services/auth.js';
      import { rbg } from '/src/utils/rbg.js';
      import { toast } from '/src/services/toast.js';

      document.addEventListener('alpine:init', () => {
        Alpine.data('rbg', rbg);
        // Navigation component
        Alpine.data('navigation', () => ({
          isAuthenticated: false,
          username: '',
          loggingOut: false,

          async init() {
            const { authenticated, user } = await session.checkAuth();
            this.isAuthenticated = authenticated;
            if (user) {
              this.username = user.username;
            }
          },

          async handleContentSwith() {
            const { authenticated, user } = await session.checkAuth();
            this.isAuthenticated = authenticated;
            if (user && this.contentType !== 'movie') {
              this.contentType = 'movie';
            } else if (user && this.contentType !== 'tv') {
              this.contentType = 'tv';
            } else {
              this.contentType = 'all';
            }
          },

          async handleLogout() {
            this.loggingOut = true;
            try {
              await auth.logout();
              toast.success('Logged out successfully');
              setTimeout(() => {
                window.location.reload();
              }, 500);
            } catch (error) {
              console.error('Logout failed:', error);
              toast.error('Logout failed');
              this.loggingOut = false;
            }
          },
        }));

        // Home page component
        Alpine.data('homePage', () => ({
          isAuthenticated: false,
          searchQuery: '',
          contentType: 'movie',
          searching: false,
          hasSearched: false,
          lastQuery: '',
          results: [],
          errorMessage: '',

          // Category state
          categories: [],
          categoriesLoading: true,
          selectedCategory: 'movies',

          // PIN modal state
          pinModalOpen: false,
          pinValue: '',
          pinError: '',
          pinLoading: false,
          xxxVerified: false,
          pinResolve: null,

          async init() {
            const { authenticated } = await session.checkAuth();
            this.isAuthenticated = authenticated;

            if (authenticated) {
              session.setupAutoLogout();
              await this.loadCategories();
            }
          },

          async loadCategories() {
            this.categoriesLoading = true;
            try {
              const response = await search.getCategories();
              if (response.success) {
                this.categories = response.categories || [];
                // Default to movies if available
                if (this.categories.length > 0) {
                  this.selectedCategory = 'movies';
                  this.contentType = 'movie';
                }
              }
            } catch (error) {
              console.error('Failed to load categories:', error);
              // Fallback to basic categories
              this.categories = [
                { id: 'movies', label: 'Movies', requiresAdult: false, requiresPin: false },
                { id: 'tv', label: 'TV-Shows', requiresAdult: false, requiresPin: false },
              ];
            } finally {
              this.categoriesLoading = false;
            }
          },

          async selectCategory(categoryId) {
            const category = this.categories.find(c => c.id === categoryId);
            if (!category) return;

            // Check if XXX category requires PIN verification
            if (category.requiresPin && !this.xxxVerified) {
              const verified = await this.openPinModal();
              if (!verified) {
                return; // Don't switch if PIN not verified
              }
              this.xxxVerified = true;
            }

            this.selectedCategory = categoryId;

            // Map category to content_type for TMDB
            if (categoryId === 'movies') {
              this.contentType = 'movie';
            } else {
              this.contentType = 'tv';
            }
          },

          openPinModal() {
            return new Promise((resolve) => {
              this.pinModalOpen = true;
              this.pinValue = '';
              this.pinError = '';
              this.pinLoading = false;
              this.pinResolve = resolve;
            });
          },

          closePinModal(verified = false) {
            this.pinModalOpen = false;
            this.pinValue = '';
            this.pinError = '';
            if (this.pinResolve) {
              this.pinResolve(verified);
              this.pinResolve = null;
            }
          },

          async submitPin() {
            if (this.pinValue.length < 4 || this.pinValue.length > 6) {
              this.pinError = 'PIN must be 4-6 digits';
              return;
            }

            this.pinLoading = true;
            this.pinError = '';

            try {
              const response = await settings.verifyPin(this.pinValue);

              if (response.verified) {
                this.closePinModal(true);
                toast.success('PIN verified');
                return;
              }

              // Handle failed verification
              if (response.lockoutRemainingSeconds) {
                this.pinError = `Too many attempts. Try again in ${Math.ceil(response.lockoutRemainingSeconds / 60)} minutes.`;
              } else if (response.attemptsRemaining !== undefined) {
                this.pinError = response.error || `Incorrect PIN. ${response.attemptsRemaining} attempts remaining.`;
              } else {
                this.pinError = response.error || 'Incorrect PIN';
              }

              this.pinValue = '';
            } catch (error) {
              this.pinError = error.message || 'Verification failed. Try again.';
            } finally {
              this.pinLoading = false;
            }
          },

          async handleSearch() {
            if (!this.isAuthenticated) {
              toast.info('Please login to search');
              window.location.href = '/src/pages/Login.html';
              return;
            }

            if (!this.searchQuery.trim()) {
              this.errorMessage = 'Enter a search term';
              toast.warning('Please enter a search term');
              return;
            }

            this.searching = true;
            this.errorMessage = '';
            this.hasSearched = true;
            this.lastQuery = this.searchQuery;

            try {
              // Pass category and PIN verification status
              const response = await search.search(
                this.searchQuery,
                this.contentType,
                this.selectedCategory,
                this.xxxVerified
              );

              // Check if PIN is required
              if (response.requires_pin) {
                const verified = await this.openPinModal();
                if (verified) {
                  this.xxxVerified = true;
                  // Retry search with PIN verified
                  return this.handleSearch();
                } else {
                  this.searching = false;
                  return;
                }
              }

              if (!response.success) {
                this.errorMessage = response.error || 'Search failed';
                this.results = [];
                toast.error(this.errorMessage);
                return;
              }

              this.results = response.results || [];
              if (this.results.length === 0) {
                toast.info(`No results found for "${this.searchQuery}"`);
              } else {
                toast.success(`Found ${this.results.length} results`);
              }
            } catch (error) {
              this.errorMessage = error.message || 'Search failed. Try again.';
              this.results = [];
              toast.error(this.errorMessage);
            } finally {
              this.searching = false;
            }
          },
        }));
      });

      window.Alpine = Alpine;
      Alpine.start();
    